<template>
  <div class="design-cockpit-step3 oring-card">
    <div class="section-header">
      <h4 class="section-title">
        {{ $tv('installationSpaceDimensions','ORingConfigurator') }}
      </h4>
    </div>
    <ToleranceChecker
      ref="toleranceCheckerRef"
      :tolerance-data="toleranceData"
      :parameter-input-data="parameterInputData"
      @update:tolerance-data="(value) => { oringStore.toleranceData = value }"
      @update:parameter-input-data="(value) => { oringStore.parameterInputData = value }"
    />
  </div>
</template>

<script setup lang="ts">
import ToleranceChecker from '../ToleranceChecker.vue'
import {useORingCalculatorStore} from '@/store/oring-calculator'

// Template ref for ToleranceChecker component
const toleranceCheckerRef = ref<InstanceType<typeof ToleranceChecker> | null>(null)

// Connect to store
const oringStore = useORingCalculatorStore()

// Call createHousingExpertMode when entering this step to get a fresh object
onMounted(async () => {
  const hasExistingParameterData = Object.keys(oringStore.parameterInputData || {}).some(key =>
    oringStore.parameterInputData?.[key] !== null && oringStore.parameterInputData?.[key] !== undefined
  )
  const hasExistingToleranceData = Object.keys(oringStore.toleranceData || {}).some(key =>
    oringStore.toleranceData?.[key] !== null && oringStore.toleranceData?.[key] !== undefined
  )

  // Only create housing if we don't have existing data or if we don't have a current housing
  if (!hasExistingParameterData && !hasExistingToleranceData && !oringStore.oringDesignCockpitCurrentHousing) {
    await oringStore.createHousingExpertMode()
  }
})

// Use store data as the model - now storing tolerance class IDs by parameter key
const toleranceData = computed({
  get: () => oringStore.toleranceData || {},
  set: (value) => {
    oringStore.toleranceData = value
  }
})

// Use store data for parameter input values
const parameterInputData = computed({
  get: () => oringStore.parameterInputData || {},
  set: (value) => {
    oringStore.parameterInputData = value
  }
})

// Function to fetch fresh housing data from the API
async function refreshHousingData() {
  await oringStore.createHousingExpertMode()
}

function validate(): boolean {
  // Call the ToleranceChecker's validate method
  return toleranceCheckerRef.value?.validate() ?? false
}

defineExpose({
  validate,
  refreshHousingData // Expose this method so parent components can trigger a refresh if needed
})
</script>

<style lang="scss" scoped>
.design-cockpit-step3 {
  margin: 1rem 0;

  &.oring-card {
    max-width: unset;
  }
}

.section-header {
  margin-bottom: .5rem;
}

.section-title {
  margin-top: 0;
}
</style>
